import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { queryClient } from "./lib/queryClient";
import { ensurePushServiceWorkerRegistered } from "./lib/push";
import { SessionProvider } from "./state/session";
import { ToastHost } from "@zentrani/ui";
import "./styles.css";

// App only (push taps navigate inside the SPA). Inline env check: not in the web build.
const NativeRouterBridge =
  import.meta.env.VITE_PLATFORM === "native"
    ? lazy(() => import("./native/NativeRouterBridge").then((m) => ({ default: m.NativeRouterBridge })))
    : null;

function render() {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            {NativeRouterBridge ? (
              <Suspense fallback={null}>
                <NativeRouterBridge />
              </Suspense>
            ) : null}
            <App />
            <ToastHost />
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  );
}

// Inline env check (not NATIVE_BUILD) so the web build drops the native chunk entirely.
if (import.meta.env.VITE_PLATFORM === "native") {
  // Android app: no service worker; restore the Bearer session before the first request.
  void import("./native/bootstrap")
    .then(({ bootstrapNative }) => bootstrapNative())
    // Never leave a blank screen: render signed out if the native setup fails.
    .catch(() => ({ afterRender: () => {} }))
    .then(({ afterRender }) => {
      render();
      afterRender();
    });
} else {
  ensurePushServiceWorkerRegistered();
  render();
}
